import React, { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Card } from '../ui';
import WorldMap from './WorldMap';
import LevelChanges from './LevelChanges';
import { CountryRow, CountryTile, LEVELS, LEVEL_COLORS, NewsFeed, TFlag, ScoreBadge } from './parts';

function Filters({ q, setQ, lvl, setLvl }) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <label className="flex items-center gap-2 border border-[#1F242C] rounded-sm px-3 h-9 w-full sm:w-64">
        <Search size={14} className="text-[#8A8F97]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca un Paese" className="bg-transparent outline-none text-sm text-[#F2F2F2] flex-1 min-w-0" data-testid="travel-search" />
      </label>
      {[0, 1, 2, 3, 4].map((l) => (
        <button key={l} onClick={() => setLvl(l)} className={`h-9 px-3 text-xs rounded-sm border transition-colors ${lvl === l ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-[#1F242C] text-[#8A8F97] hover:text-[#F2F2F2]'}`} data-testid={`travel-filter-${l}`}>
          {l ? <span className="inline-flex items-center gap-1.5"><i className="w-2 h-2 rounded-full" style={{ background: LEVEL_COLORS[l] }} />Livello {l}</span> : 'Tutti'}
        </button>
      ))}
    </div>
  );
}

function useFiltered(countries) {
  const [q, setQ] = useState('');
  const [lvl, setLvl] = useState(0);
  const list = useMemo(() => countries.filter((c) => (c.name || '').toLowerCase().includes(q.toLowerCase()) && (!lvl || c.farnesina_level === lvl)), [countries, q, lvl]);
  return { list, f: { q, setQ, lvl, setLvl } };
}

function ListCard({ countries, onOpen, height = 'max-h-[560px]' }) {
  const { list, f } = useFiltered(countries);
  return (
    <Card className="p-6" data-testid="travel-list">
      <p className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-4">Paesi · indicazione Farnesina e indice BOND 1-10</p>
      <Filters {...f} />
      <div className={`${height} overflow-y-auto -mx-2`}>{list.map((c) => <CountryRow key={c.iso3} c={c} onOpen={onOpen} />)}</div>
    </Card>
  );
}

export function LayoutAtlante({ countries, news, changes, onOpen }) {
  return (
    <>
      <WorldMap countries={countries} onOpen={onOpen} />
      <LevelChanges items={changes} onOpen={onOpen} />
      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-4"><ListCard countries={countries} onOpen={onOpen} /><NewsFeed news={news} /></div>
    </>
  );
}

export function LayoutOperativa({ countries, news, changes, onOpen }) {
  return (
    <div className="grid xl:grid-cols-[2fr_1fr] gap-4 items-start">
      <div><WorldMap countries={countries} onOpen={onOpen} /><ListCard countries={countries} onOpen={onOpen} height="max-h-[420px]" /></div>
      <div><LevelChanges items={changes} onOpen={onOpen} compact /><NewsFeed news={news} max={6} /></div>
    </div>
  );
}

export function LayoutSchede({ countries, news, changes, onOpen }) {
  const { list, f } = useFiltered(countries);
  return (
    <>
      <div className="grid xl:grid-cols-[1.4fr_1fr] gap-4 items-start"><WorldMap countries={countries} onOpen={onOpen} /><LevelChanges items={changes} onOpen={onOpen} compact /></div>
      <Card className="p-6 mb-4" data-testid="travel-tiles">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-4">Schede Paese</p>
        <Filters {...f} />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6 gap-3 max-h-[720px] overflow-y-auto pr-1">{list.map((c) => <CountryTile key={c.iso3} c={c} onOpen={onOpen} />)}</div>
      </Card>
      <NewsFeed news={news} />
    </>
  );
}

export function LayoutFasce({ countries, news, changes, onOpen }) {
  return (
    <>
      <WorldMap countries={countries} onOpen={onOpen} />
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4" data-testid="travel-bands">
        {[4, 3, 2, 1].map((l) => {
          const items = countries.filter((c) => c.farnesina_level === l);
          return (
            <Card key={l} className="p-0 overflow-hidden" data-testid={`travel-band-${l}`}>
              <div className="px-4 py-3 flex items-center justify-between" style={{ background: LEVEL_COLORS[l] }}>
                <span className="text-xs font-bold text-white uppercase tracking-wider">{LEVELS[l]}</span><span className="text-lg font-bold text-white">{items.length}</span>
              </div>
              <div className="max-h-[380px] overflow-y-auto">
                {items.map((c) => (
                  <button key={c.iso3} onClick={() => onOpen(c.iso3)} className="w-full grid grid-cols-[28px_minmax(0,1fr)_40px] gap-2 items-center px-4 py-2 border-t border-[#1F242C] text-left hover:bg-[#D4AF37]/5 transition-colors" data-testid={`travel-band-country-${c.iso3}`}>
                    <TFlag iso2={c.iso2} /><span className="text-sm text-[#F2F2F2] truncate" title={c.name}>{c.name}</span><ScoreBadge score={c.score == null ? null : Math.round(c.score)} />
                  </button>
                ))}
              </div>
            </Card>
          );
        })}
      </div>
      <div className="grid lg:grid-cols-2 gap-4"><LevelChanges items={changes} onOpen={onOpen} compact /><NewsFeed news={news} /></div>
    </>
  );
}

export const LAYOUTS = [
  ['atlante', 'A · Atlante', LayoutAtlante],
  ['operativa', 'B · Sala operativa', LayoutOperativa],
  ['schede', 'C · Schede Paese', LayoutSchede],
  ['fasce', 'D · Fasce di rischio', LayoutFasce],
];
